import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import PlaylistAdd from "@mui/icons-material/PlaylistAdd";
import { useCallback, useState } from "react";
import { isChatChannel, isEmailChannel } from "../../../../channels/types";
import { AddButtonListForm } from "../../components/button/add-button-list-form";
import { AddStylableButtonForm } from "../../components/button/add-styleable-button-form";
import { useSurveysEnabledLexical } from "../../components/hooks/use-surveys-enabled";
import { useLexicalContext } from "../../lexical-context";
import { INSERT_BUTTON_LIST_COMMAND, INSERT_STYLABLE_BUTTON_COMMAND } from "../../types/commands";
import { ToolbarIconButton } from "./toolbar-icon-button";

import type { ChannelName } from "../../../../channels/types";
import type { TagGroup } from "../../../../merge-tag/components/types";
import type { ButtonNodeBlock } from "../../types/markdown-node-blocks";
import type { StylableButtonForm } from "../../types/styleable-button";
import type { ToolbarProps } from "./props";
import type { FC } from "react";

type Props = ToolbarProps & {
  filterTags: (x: string) => TagGroup;
  label?: string;
  channel: NonNullable<ChannelName>;
};

export const AddSurveyButton: FC<Props> = ({ filterTags, disabled, label, onClick, channel }) => {
  const [showModal, setShowModal] = useState(false);

  const surveyjs = useSurveysEnabledLexical();
  const { editorModal } = useLexicalContext();
  const [editor] = useLexicalComposerContext();

  const handleChatSave = useCallback(
    (data: { buttons: ButtonNodeBlock[] }): void => {
      if (data) editor.dispatchCommand(INSERT_BUTTON_LIST_COMMAND, data);
      setShowModal(false);
      onClick?.();
    },
    [onClick, editor],
  );

  const handleEmailSave = useCallback(
    (data: StylableButtonForm): void => {
      if (data) editor.dispatchCommand(INSERT_STYLABLE_BUTTON_COMMAND, data);
      setShowModal(false);
      onClick?.();
    },
    [onClick, editor],
  );

  return (
    <>
      <ToolbarIconButton
        data-analytics-id="lexical-toolbar-add-survey"
        sx={{ justifyContent: "start" }}
        disabled={disabled}
        label={label}
        icon={<PlaylistAdd fontSize="medium" />}
        data-testid="toolbar-button-add-survey"
        aria-label="Add Survey"
        tabIndex={-1}
        onClick={() => {
          if (channel === "twilio") {
            editorModal.handleOpen({
              type: "link",
              data: {
                mode: "add",
                type: "survey_link",
                url: undefined,
              },
            });
          } else {
            // TODO change the rest of them to use the global context dialog.
            setShowModal(true);
          }
        }}
      />

      {isEmailChannel(channel) && showModal && (
        <AddStylableButtonForm
          open={showModal}
          onCancel={() => {
            setShowModal(false);
            if (onClick) onClick();
          }}
          initialState={{
            text: "",
            url: "",
            buttonType: "primary_button",
            fullWidth: false,
            format: "left",
          }}
          onConfirm={handleEmailSave}
          filterTags={filterTags}
          defaultLinkType={{
            type: "link_survey",
            channel: surveyjs.enabled && surveyjs.type === "channel",
          }}
        />
      )}
      {isChatChannel(channel) && showModal && (
        <AddButtonListForm
          open={showModal}
          onCancel={() => {
            setShowModal(false);
            if (onClick) onClick();
          }}
          onConfirm={handleChatSave}
          disabled={disabled}
          filterTags={filterTags}
          defaultLinkType={{
            type: "link_survey",
            channel: surveyjs.enabled && surveyjs.type === "channel",
          }}
        />
      )}
    </>
  );
};
